import React from "react";
import { useEvent } from "../hooks/useEvent";
import styled from "styled-components";
import { style } from "../style";
import { Icon } from "../Icon";

type Tab = {
  title: React.ReactNode;
  value: string;
};

type TabProps = {
  option: Tab;
  onSelect(value: string): void;
  selected: boolean;

  first: boolean;
  last: boolean;
};

type InnerTabProps = {
  $selected: boolean;
  $first: boolean;
  $last: boolean;
};

const TabList = styled.div<{ $compact: boolean }>`
  display: ${(props) => (props.$compact ? "inline-flex" : "flex")};
  flex-wrap: nowrap;
`;

const TabContainer = styled.div`
  display: flex;
  align-items: center;
  flex-grow: 1;
`;

const InnerTab = styled.button<InnerTabProps>`
  all: unset;

  flex-grow: 1;

  cursor: pointer;

  background-color: ${style.colors.white};
  color: ${style.colors.grey.darkest};
  font-size: ${style.vars.fontSize.small};
  font-weight: ${style.vars.fontWeight.bold};

  height: ${style.vars.height.small};
  padding: 0 ${style.vars.space["2"]};

  border: 1px solid ${style.colors.grey.primary};

  ${(props) => {
    const width = {
      left: "0px",
      right: "0px",
    };

    const radius = {
      topLeft: "0px",
      topRight: "0px",
      bottomRight: "0px",
      bottomLeft: "0px",
    };

    if (props.$first) {
      width.left = "1px";
      radius.topLeft = style.vars.borderRadius.base;
      radius.bottomLeft = style.vars.borderRadius.base;
    }

    if (props.$last) {
      width.right = "1px";
      radius.topRight = style.vars.borderRadius.base;
      radius.bottomRight = style.vars.borderRadius.base;
    }

    return {
      borderRadius: `${radius.topLeft} ${radius.topRight} ${radius.bottomRight} ${radius.bottomLeft}`,
      borderRightWidth: width.right,
      borderLeftWidth: width.left,
    };
  }}

  &:disabled {
    cursor: not-allowed;
  }

  &:focus {
    color: ${style.colors.primary.base};
  }

  &:hover {
    color: ${style.colors.primary.base};
    background-color: ${style.colors.primaryBackground};
    border-color: ${style.colors.primary.base};
  }

  ${(props) => {
    if (props.$selected) {
      return {
        color: style.colors.primary.base,
        backgroundColor: style.colors.primaryBackground,
        borderColor: style.colors.primary.base,
      };
    }
  }}
`;

const TabDivider = styled.div<{ $highlight: boolean }>`
  display: inline-block;
  width: 1px;
  height: calc(${style.vars.height.small} + 2px);
  background-color: ${(props) => (props.$highlight ? style.colors.primary.base : style.colors.grey.primary)};

  flex-grow: 0;
`;

const IconDivider = styled.div`
  display: inline-block;
  margin-left: ${style.vars.space["2"]};
`;

const Tab: React.FC<TabProps> = (props) => {
  const { option, selected, onSelect, first, last } = props;

  const onClick = useEvent<React.MouseEventHandler>((ev) => {
    ev.preventDefault();
    onSelect(option.value);
  });

  return (
    <InnerTab $first={first} $last={last} $selected={selected} onClick={(ev) => onClick(ev)}>
      {option.title}
      {selected ? (
        <>
          <IconDivider />
          <Icon icon="check" />
        </>
      ) : null}
    </InnerTab>
  );
};

export type Props = {
  options: Tab[];
  onSelect(value: string): void;
  selected?: string;
  compact?: boolean;
};

export const ButtonTabs: React.FC<Props> = (props) => {
  const { options, selected, onSelect, compact = false } = props;
  const [hovered, setHovered] = React.useState<string | null>(null);

  return (
    <TabList role="tablist" onMouseLeave={() => setHovered(null)} $compact={compact}>
      {options.map((option, index) => {
        const nextTab = options[index + 1];
        const isLast = index === options.length - 1;

        const currentSelected = option.value === selected;
        const currentHovered = hovered === option.value;

        let nextSelected = false;
        let nextHovered = false;

        if (nextTab) {
          nextSelected = nextTab.value === selected;
          nextHovered = nextTab.value === hovered;
        }

        return (
          <TabContainer key={option.value} onMouseEnter={() => setHovered(option.value)}>
            <Tab option={option} onSelect={onSelect} selected={currentSelected} first={index === 0} last={isLast} />
            {/**
             * Rather than having a border right on the Tab, we add a divider so that the border between two tabs is highlighted.
             * If we don't do this, then the border between the two tabs will only change color when the **owning tab** is hovered.
             **/}
            {!isLast && <TabDivider $highlight={currentSelected || nextSelected || currentHovered || nextHovered} />}
          </TabContainer>
        );
      })}
    </TabList>
  );
};

ButtonTabs.displayName = "ButtonsTab";
